/**
 * First-touch ad click attribution in the browser (HAL-1969).
 *
 * A Discovery Report lead is the paid experiment's primary conversion, and the
 * report runs on `/discovery-report/{id}`, a page the ad never pointed at. So
 * the click ID and UTMs are captured on whichever page the click landed on,
 * kept in `localStorage` for 30 days, and read back when the visitor submits a
 * check or books a call. A visitor who clicks an ad, leaves, and comes back
 * directly a week later still carries that click.
 *
 * First touch wins: while an unexpired attribution is stored, a later
 * param-bearing visit does not replace it – with one exception. A visit that
 * carries a click ID replaces a stored record that has none. Otherwise an
 * earlier UTM-tagged visit (an email or LinkedIn link) would swallow a later
 * ad click, the lead would be credited to the email, and the ad platform would
 * never receive the conversion.
 *
 * Every storage access is wrapped in try/catch, so a check or a booking can
 * never fail because of this module. A browser with storage blocked could
 * never have stored a click, so a read there falls back to the current page's
 * own parameters: the visitor who submits on the page the ad landed on is
 * still attributed. Values are bounded here to the shared schema so
 * create-check, which rejects an invalid `attribution`, never sees one.
 */

import {
  AdClickAttributionSchema,
  AD_ATTRIBUTION_PARAM_MAX_LENGTH,
  AD_ATTRIBUTION_URL_MAX_LENGTH,
  AD_ATTRIBUTION_URL_PARAMS,
  type AdClickAttribution,
} from '@marketing-optimizer/ontology';

export const AD_ATTRIBUTION_STORAGE_KEY = 'halcy_ad_attribution';

/** How long a captured click keeps attributing, measured from `clicked_at`. */
export const AD_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Build an attribution from a page URL, or `null` when the URL carries none of
 * the click-ID or UTM parameters. An empty or oversize parameter is dropped
 * rather than truncated: a truncated click ID matches no click.
 */
export function attributionFromUrl(
  url: URL,
  referrer: string,
  now: Date,
): AdClickAttribution | null {
  const params: Partial<Record<(typeof AD_ATTRIBUTION_URL_PARAMS)[number], string>> = {};
  for (const name of AD_ATTRIBUTION_URL_PARAMS) {
    const value = url.searchParams.get(name)?.trim();
    if (value && value.length <= AD_ATTRIBUTION_PARAM_MAX_LENGTH) params[name] = value;
  }
  if (Object.keys(params).length === 0) return null;

  const parsed = AdClickAttributionSchema.safeParse({
    ...params,
    landing_path: url.pathname.slice(0, AD_ATTRIBUTION_URL_MAX_LENGTH),
    ...(referrer && referrer.length <= AD_ATTRIBUTION_URL_MAX_LENGTH && { referrer }),
    clicked_at: now.toISOString(),
  });
  return parsed.success ? parsed.data : null;
}

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const satisfies readonly (keyof AdClickAttribution)[];

function hasClickId(attribution: AdClickAttribution): boolean {
  return CLICK_ID_PARAMS.some((name) => attribution[name] !== undefined);
}

function fromCurrentPage(now: Date): AdClickAttribution | null {
  try {
    return attributionFromUrl(new URL(window.location.href), document.referrer, now);
  } catch {
    return null;
  }
}

/**
 * The stored first-touch attribution, or `null` when none is stored, it has
 * expired, or it no longer validates. An expired or invalid entry is removed
 * so the next param-bearing visit can take its place. When storage itself is
 * unavailable, the current page's parameters stand in for the stored record.
 */
export function readAdAttribution(now: Date = new Date()): AdClickAttribution | null {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY);
  } catch {
    return fromCurrentPage(now);
  }
  if (raw === null) return null;
  try {
    const parsed = AdClickAttributionSchema.safeParse(JSON.parse(raw));
    if (parsed.success && Date.parse(parsed.data.clicked_at) + AD_ATTRIBUTION_TTL_MS > now.getTime()) {
      return parsed.data;
    }
    window.localStorage.removeItem(AD_ATTRIBUTION_STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

/**
 * Capture the current page load's click IDs and UTMs, unless an unexpired
 * attribution is already stored and this visit does not bring the click ID it
 * lacks. Call once per page load. Never throws.
 */
export function captureAdAttribution(now: Date = new Date()): void {
  try {
    const fresh = attributionFromUrl(new URL(window.location.href), document.referrer, now);
    if (!fresh) return;
    const stored = readAdAttribution(now);
    if (stored && (hasClickId(stored) || !hasClickId(fresh))) return;
    window.localStorage.setItem(AD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(fresh));
  } catch {
    // Storage blocked or full: nothing is stored for this visit. Blocked
    // storage is covered at read time by readAdAttribution's URL fallback.
  }
}
